/**
 * SimulationPanel Component
 *
 * Main UI panel for running EnergyPlus simulations with:
 * - Progress bar and phase indicator
 * - Real-time console output
 * - Time estimation
 * - Control buttons (Run, Cancel)
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { formatDuration } from '../progress';
import {
  useSimulationStore,
  selectIsRunning,
  selectCanRun,
  selectIsMockMode,
} from '../simulationStore';
import type { ConsoleLine } from '../progress';

/**
 * Get CSS class for console line type
 */
function getLineClass(type: ConsoleLine['type']): string {
  switch (type) {
    case 'fatal':
      return 'bg-red-900/50 text-red-200';
    case 'severe':
      return 'bg-orange-900/50 text-orange-200';
    case 'warning':
      return 'bg-yellow-900/50 text-yellow-200';
    case 'success':
      return 'text-green-400';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Format timestamp for console display
 */
function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Console output component
 */
function ConsoleOutput() {
  const consoleRef = useRef<HTMLDivElement>(null);
  const consoleLines = useSimulationStore((state) => state.consoleLines);
  const autoScroll = useSimulationStore((state) => state.autoScroll);
  const toggleAutoScroll = useSimulationStore((state) => state.toggleAutoScroll);
  const clearConsole = useSimulationStore((state) => state.clearConsole);
  const copyConsole = useSimulationStore((state) => state.copyConsole);
  const [showTimestamps, setShowTimestamps] = useState(true);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleLines, autoScroll]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
        <span className="text-sm font-medium text-foreground">Console Output</span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showTimestamps}
              onChange={() => setShowTimestamps(!showTimestamps)}
              className="w-3 h-3"
            />
            Time
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={toggleAutoScroll}
              className="w-3 h-3"
            />
            Auto-scroll
          </label>
          <button
            onClick={clearConsole}
            className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded"
          >
            Clear
          </button>
          <button
            onClick={copyConsole}
            className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded"
          >
            Copy
          </button>
        </div>
      </div>
      <div
        ref={consoleRef}
        className="flex-1 overflow-auto bg-background font-mono text-xs p-2"
      >
        {consoleLines.length === 0 ? (
          <div className="text-muted-foreground italic">No output yet...</div>
        ) : (
          consoleLines.map((line, index) => (
            <div
              key={index}
              className={cn('px-1 py-0.5 rounded flex gap-2', getLineClass(line.type))}
            >
              {showTimestamps && (
                <span className="text-muted-foreground/60 select-none shrink-0">
                  [{formatTimestamp(line.timestamp)}]
                </span>
              )}
              <span className="break-all">{line.text}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Progress bar component
 */
function ProgressBar() {
  const progress = useSimulationStore((state) => state.progress);
  const phase = useSimulationStore((state) => state.phase);
  const progressMessage = useSimulationStore((state) => state.progressMessage);
  const timeEstimate = useSimulationStore((state) => state.timeEstimate);

  // Get phase label
  const phaseLabel = {
    initializing: 'Initializing',
    warmup: 'Warmup',
    sizing: 'Sizing',
    simulation: 'Running',
    postprocess: 'Post-processing',
    complete: 'Complete',
    error: 'Error',
  }[phase];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{phaseLabel}</span>
        <span className="text-muted-foreground">{progress.toFixed(0)}%</span>
      </div>

      <div className="h-3 bg-secondary rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full transition-all duration-300 rounded-full',
            phase === 'error' ? 'bg-red-500' : 'bg-blue-500'
          )}
          style={{ width: `${String(Math.max(0, Math.min(100, progress)))}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="truncate max-w-[60%]">{progressMessage}</span>
        <div className="flex gap-3">
          <span>Elapsed: {formatDuration(timeEstimate.elapsed)}</span>
          {timeEstimate.remaining !== null && (
            <span>Remaining: ~{formatDuration(timeEstimate.remaining)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Control buttons component
 */
function ControlButtons() {
  const isRunning = useSimulationStore(selectIsRunning);
  const canRun = useSimulationStore(selectCanRun);
  const startSimulation = useSimulationStore((state) => state.startSimulation);
  const cancelSimulation = useSimulationStore((state) => state.cancelSimulation);
  const forceCancel = useSimulationStore((state) => state.forceCancel);
  const reset = useSimulationStore((state) => state.reset);
  const status = useSimulationStore((state) => state.status);
  const timeEstimate = useSimulationStore((state) => state.timeEstimate);

  // Emphasize force cancel if running for more than 30 seconds
  // This helps when simulation appears hung
  const emphasizeForceCancel = isRunning && timeEstimate.elapsed > 30000;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isRunning ? (
        <>
          <button
            onClick={cancelSimulation}
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm sm:text-base font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={forceCancel}
            className={cn(
              'px-3 py-1.5 sm:px-4 sm:py-2 rounded text-sm sm:text-base font-medium transition-all',
              emphasizeForceCancel
                ? 'bg-red-800 hover:bg-red-900 text-white'
                : 'bg-transparent border border-red-600/50 text-red-400 hover:bg-red-900/30 hover:border-red-500'
            )}
            title="Force terminate the simulation worker. Use if simulation appears hung."
          >
            Force Stop
          </button>
        </>
      ) : (
        <button
          onClick={startSimulation}
          disabled={!canRun}
          className={cn(
            'px-3 py-1.5 sm:px-4 sm:py-2 rounded text-sm sm:text-base font-medium transition-colors',
            canRun
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          )}
        >
          Run Simulation
        </button>
      )}

      {(status === 'complete' || status === 'error' || status === 'cancelled') && (
        <button
          onClick={reset}
          className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm sm:text-base font-medium transition-colors"
        >
          Reset
        </button>
      )}
    </div>
  );
}

/**
 * Status indicator component
 */
function StatusIndicator() {
  const status = useSimulationStore((state) => state.status);
  const error = useSimulationStore((state) => state.error);
  const isMockMode = useSimulationStore(selectIsMockMode);
  const wasmLoaded = useSimulationStore((state) => state.wasmLoaded);
  const timeEstimate = useSimulationStore((state) => state.timeEstimate);

  const statusConfig = {
    idle: { color: 'bg-gray-500', label: 'Ready', pulse: false },
    loading: { color: 'bg-yellow-500', label: 'Loading WASM...', pulse: true },
    ready: { color: 'bg-green-500', label: 'Ready', pulse: false },
    running: { color: 'bg-blue-500', label: 'Running', pulse: true },
    complete: { color: 'bg-green-500', label: 'Complete', pulse: false },
    error: { color: 'bg-red-500', label: 'Error', pulse: false },
    cancelled: { color: 'bg-orange-500', label: 'Cancelled', pulse: false },
  };

  const config = statusConfig[status];

  // Check if simulation might be stuck (running for more than 60s without progress change)
  const mightBeStuck = status === 'running' && timeEstimate.elapsed > 60000;

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'w-3 h-3 rounded-full',
          config.color,
          config.pulse && 'animate-pulse'
        )}
      />
      <span className="text-sm text-muted-foreground">{config.label}</span>
      {wasmLoaded && (
        <span
          className={cn(
            'px-2 py-0.5 text-xs font-medium rounded',
            isMockMode
              ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-600'
              : 'bg-green-900/50 text-green-300 border border-green-600'
          )}
          title={isMockMode ? 'Using mock simulation (WASM not available)' : 'Using real EnergyPlus WASM engine'}
        >
          {isMockMode ? 'Mock Mode' : 'WASM'}
        </span>
      )}
      {mightBeStuck && (
        <span
          className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-900/50 text-yellow-300 border border-yellow-600"
          title="If the simulation appears stuck, use the Force Stop button to terminate it"
        >
          Long-running
        </span>
      )}
      {error && (
        <span className="text-sm text-red-400 truncate max-w-md">
          : {error}
        </span>
      )}
    </div>
  );
}

/**
 * Error summary component
 */
function ErrorSummary() {
  const output = useSimulationStore((state) => state.output);

  if (!output?.err) return null;

  const { warningCount, severeCount, fatalCount, simulationSuccessful } = output.err;

  return (
    <div
      className={cn(
        'p-3 rounded',
        simulationSuccessful ? 'bg-green-900/30' : 'bg-red-900/30'
      )}
    >
      <div className="flex items-center gap-4 text-sm">
        <span
          className={cn(
            'font-medium',
            simulationSuccessful ? 'text-green-400' : 'text-red-400'
          )}
        >
          {simulationSuccessful ? 'Simulation Successful' : 'Simulation Failed'}
        </span>
        <div className="flex gap-4 text-muted-foreground">
          {warningCount > 0 && (
            <span className="text-yellow-400">{warningCount} warnings</span>
          )}
          {severeCount > 0 && (
            <span className="text-orange-400">{severeCount} severe</span>
          )}
          {fatalCount > 0 && (
            <span className="text-red-400">{fatalCount} fatal</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Main SimulationPanel component
 */
export function SimulationPanel() {
  const status = useSimulationStore((state) => state.status);
  const wasmLoaded = useSimulationStore((state) => state.wasmLoaded);
  const loadWasm = useSimulationStore((state) => state.loadWasm);

  // Load WASM on mount
  useEffect(() => {
    if (!wasmLoaded) {
      void loadWasm();
    }
  }, [wasmLoaded, loadWasm]);

  return (
    <div className="flex flex-col h-full bg-card rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-4 min-w-0">
          <h2 className="text-lg font-semibold text-foreground shrink-0">Simulation</h2>
          <StatusIndicator />
        </div>
        <ControlButtons />
      </div>

      {/* Progress */}
      {(status === 'running' || status === 'complete') && (
        <div className="px-4 py-3 border-b border-border">
          <ProgressBar />
        </div>
      )}

      {/* Error Summary */}
      {status === 'complete' && (
        <div className="px-4 py-3 border-b border-border">
          <ErrorSummary />
        </div>
      )}

      {/* Console */}
      <div className="flex-1 min-h-0">
        <ConsoleOutput />
      </div>
    </div>
  );
}

export default SimulationPanel;
